import {
  BufferGeometry,
  type Color,
  Float32BufferAttribute,
  Group,
  Line,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  type MeshToonMaterial,
} from 'three';
import { arcDist, bridgeHalf, CuttingQuery } from '../track/crossing';
import type { TrackData } from '../track/types';
import { look } from './look';
import { mix, PAL } from './palette';
import { toonMaterial } from './toon';

/**
 * Meshes generated from the spline: asphalt ribbon (with a rubbered-in racing
 * line), painted edge lines, raised red/cream curbs, gravel traps, the grass
 * runoff out to the barriers and the chequered start/finish line. Everything
 * follows the road's height. Sim (x, y) maps to three.js (x, h, -y).
 */
const Y_ASPHALT = 0.03;
const Y_LINE = 0.035;
const Y_GRAVEL = 0.02;
const Y_APRON = 0.028;
const Y_GROUND = 0.012;

function trackMaterial(): MeshToonMaterial {
  const m = toonMaterial(0xffffff, { vertexColors: true });
  m.polygonOffset = true;
  m.polygonOffsetFactor = -2;
  m.polygonOffsetUnits = -2;
  return m;
}

class Tris {
  pos: number[] = [];
  col: number[] = [];
  quad(a: number[], b: number[], c: number[], d: number[], color: Color): void {
    for (const p of [a, b, c, a, c, d]) {
      this.pos.push(p[0], p[1], p[2]);
      this.col.push(color.r, color.g, color.b);
    }
  }
  mesh(mat: MeshToonMaterial): Mesh {
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(this.pos, 3));
    g.setAttribute('color', new Float32BufferAttribute(this.col, 3));
    g.computeVertexNormals();
    const m = new Mesh(g, mat);
    m.receiveShadow = true;
    return m;
  }
}

/** A point on the road: `o` metres left of the centreline at sample i (wrapping), `y` above the road, in three.js coordinates. */
export type RoadPoint = (i: number, o: number, y: number) => number[];

/**
 * How every road mesh places its points: along the elevation, and tilted with
 * the banking (curbs included); beyond the road's edges the ground carries on level.
 */
export function roadPoint(t: TrackData): RoadPoint {
  const n = t.n;
  const bank = t.bank ?? null;
  const edge = t.halfWidth + t.curbWidth;
  return (i, o, y) => {
    const k = ((i % n) + n) % n;
    const tilt = bank ? (o < -edge ? -edge : o > edge ? edge : o) * bank[k] : 0;
    return [t.px[k] - t.ty[k] * o, y + t.elev[k] + tilt, -(t.py[k] + t.tx[k] * o)];
  };
}

/**
 * Where the asphalt's five bands meet across the road at a sample, right to
 * left: edge line, tarmac, the rubbered-in racing line (2.4 m wide, kept off
 * the edge lines), tarmac, edge line. Each band is a quad from one sample to
 * the next, split on its diagonal from the right-hand stop here to the
 * left-hand stop at the next sample.
 */
export function asphaltStops(hw: number, lineOffset: number): number[] {
  const a = Math.max(-hw + 1.6, Math.min(hw - 1.6, lineOffset));
  return [-hw, -hw + 0.28, a - 1.2, a + 1.2, hw - 0.28, hw];
}

export function buildTrackMeshes(t: TrackData, lineOffset: number[]): Group {
  const group = new Group();
  const n = t.n;
  const hw = t.halfWidth;
  const cw = t.curbWidth;
  const P = roadPoint(t);
  /** A strip between inner offsets (a0 at i, a1 at i + 1) and outer ones (b0, b1) on one side, faces up. */
  const band = (tris: Tris, side: number, i: number, a0: number, a1: number, b0: number, b1: number, ya: number, yb: number, color: Color): void => {
    if (side > 0) tris.quad(P(i, a0, ya), P(i + 1, a1, ya), P(i + 1, b1, yb), P(i, b0, yb), color);
    else tris.quad(P(i, -b0, yb), P(i + 1, -b1, yb), P(i + 1, -a1, ya), P(i, -a0, ya), color);
  };

  // --- runoff ground: grass at the road's own height out past the barrier,
  // then a short skirt that dips under the terrain (which sits a little lower here).
  const ground = new Tris();
  // A crossover: no grass on the bridge (its deck is there), none hanging over the cutting beside it.
  const c = t.crossing ?? null;
  const cut = c ? new CuttingQuery(t, c) : null;
  const bridge = c ? bridgeHalf(c) + 2 : 0;
  const overCut = (i: number, o: number): boolean => {
    const k = ((i % n) + n) % n;
    return !!cut && cut.inside(t.px[k] - t.ty[k] * o, t.py[k] + t.tx[k] * o, 1);
  };
  // The verge is the setting's ground (look.ts).
  const grass = look().ground.grass;
  for (const side of [1, -1]) {
    const bar = side > 0 ? t.barrierLeft : t.barrierRight;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      if (c) {
        const u = arcDist(t.s[i], c.sOver, t.length);
        if (u <= bridge) continue;
        if (u < 80 && [hw, bar[i] + 0.5, bar[i] + 3.5].some((o) => overCut(i, side * o) || overCut(i + 1, side * o))) continue;
      }
      band(ground, side, i, hw - 0.05, hw - 0.05, bar[i] + 0.5, bar[j] + 0.5, Y_GROUND, Y_GROUND, grass);
      band(ground, side, i, bar[i] + 0.5, bar[j] + 0.5, bar[i] + 3.5, bar[j] + 3.5, Y_GROUND, -0.8, grass);
    }
  }
  group.add(ground.mesh(trackMaterial()));

  // --- asphalt with edge lines and a darker rubbered-in line
  const asphalt = new Tris();
  const tarmac = PAL.asphalt;
  const rubber = mix('asphalt', 'brown', 0.12);
  const paint = PAL.cream;
  for (let i = 0; i < n; i++) {
    const stops0 = asphaltStops(hw, lineOffset[i]);
    const stops1 = asphaltStops(hw, lineOffset[(i + 1) % n]);
    const cols = [paint, tarmac, rubber, tarmac, paint];
    for (let k = 0; k < 5; k++) {
      const y = k === 0 || k === 4 ? Y_LINE : Y_ASPHALT;
      asphalt.quad(P(i, stops0[k], y), P(i + 1, stops1[k], y), P(i + 1, stops1[k + 1], y), P(i, stops0[k + 1], y), cols[k]);
    }
  }
  group.add(asphalt.mesh(trackMaterial()));

  // --- curbs: raised, red/cream stripes every 2 m
  const curbs = new Tris();
  const profile = [
    [0, 0.04],
    [0.32, 0.11],
    [cw - 0.3, 0.11],
    [cw, 0.03],
  ];
  for (const side of [1, -1]) {
    const flags = side > 0 ? t.curbLeft : t.curbRight;
    for (let i = 0; i < n; i++) {
      if (!flags[i] || !flags[(i + 1) % n]) continue;
      const color = Math.floor(t.s[i] / 2) % 2 === 0 ? PAL.red : PAL.cream;
      for (let k = 0; k < profile.length - 1; k++) {
        const o0 = side * (hw + profile[k][0]);
        const o1 = side * (hw + profile[k + 1][0]);
        const y0 = profile[k][1];
        const y1 = profile[k + 1][1];
        if (side > 0) curbs.quad(P(i, o0, y0), P(i + 1, o0, y0), P(i + 1, o1, y1), P(i, o1, y1), color);
        else curbs.quad(P(i, o1, y1), P(i + 1, o1, y1), P(i + 1, o0, y0), P(i, o0, y0), color);
      }
    }
  }
  const curbMesh = curbs.mesh(trackMaterial());
  curbMesh.castShadow = true;
  group.add(curbMesh);

  // --- gravel traps, and (modern circuits) painted tarmac runoff in the big braking zones
  const gravel = new Tris();
  const g1 = mix('cream', 'grass', 0.45);
  const g2 = mix('cream', 'grass', 0.52);
  const tarmacRun = mix('asphalt', 'cream', 0.14);
  const tarmacBand = mix('red', 'asphalt', 0.28);
  const tarmacBand2 = mix('ochre', 'asphalt', 0.35);
  for (const side of [1, -1]) {
    const run = side > 0 ? t.runoffLeft : t.runoffRight;
    const bar = side > 0 ? t.barrierLeft : t.barrierRight;
    const curb = side > 0 ? t.curbLeft : t.curbRight;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const kind = run[i];
      if ((kind !== 3 && kind !== 5) || run[j] !== kind) continue;
      const in0 = hw + (curb[i] ? cw : 0.25);
      const in1 = hw + (curb[j] ? cw : 0.25);
      const out0 = Math.max(in0 + 1, bar[i] - 0.4);
      const out1 = Math.max(in1 + 1, bar[j] - 0.4);
      let c: Color;
      if (kind === 3) c = Math.sin(i * 12.9898 + side * 78.233) * 43758.5453 % 1 > 0.4 ? g1 : g2;
      else {
        // Painted bands across the tarmac, like the modern circuits' runoff.
        const band = Math.floor(t.s[i] / 3) % 4;
        c = band === 0 ? tarmacBand : band === 2 ? tarmacBand2 : tarmacRun;
      }
      if (side > 0) gravel.quad(P(i, in0, Y_GRAVEL), P(i + 1, in1, Y_GRAVEL), P(i + 1, out1, Y_GRAVEL), P(i, out0, Y_GRAVEL), c);
      else gravel.quad(P(i, -out0, Y_GRAVEL), P(i + 1, -out1, Y_GRAVEL), P(i + 1, -in1, Y_GRAVEL), P(i, -in0, Y_GRAVEL), c);
    }
  }
  group.add(gravel.mesh(trackMaterial()));

  // --- runoff aprons beyond the curb (circuits from 30 September 2026): a flat second kerb,
  // turf (packed earth on the old circuits) or painted tarmac, where cars run to the white line.
  if (t.apronLeft && t.apronRight && t.apronWidthLeft && t.apronWidthRight) {
    const aprons = new Tris();
    const classic = t.runoffStyle === 'classic';
    // Old circuits: packed earth, reddish brown. Later ones: mown turf.
    const turfA = classic ? mix('terracotta', 'brown', 0.3) : mix('olive', 'cypress', 0.26);
    const turfB = classic ? mix('terracotta', 'brown', 0.18) : mix('olive', 'cypress', 0.4);
    const kerbA = mix('red', 'cream', 0.2);
    const kerbB = PAL.cream;
    const tarA = mix('asphalt', 'cream', 0.14);
    const tarB = mix('red', 'asphalt', 0.3);
    for (const side of [1, -1]) {
      const kind = side > 0 ? t.apronLeft : t.apronRight;
      const width = side > 0 ? t.apronWidthLeft : t.apronWidthRight;
      const curb = side > 0 ? t.curbLeft : t.curbRight;
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const k = kind[i];
        if (k === 0 || kind[j] === 0) continue;
        const in0 = hw + (curb[i] ? cw : 0.04);
        const in1 = hw + (curb[j] ? cw : 0.04);
        // The flat kerb's blocks run in step with the curb beside it: one wide kerb, not a chessboard.
        const stripe = Math.floor(t.s[i] / (k === 1 ? 2 : 3)) % 2 === 0;
        const c = k === 1 ? (stripe ? kerbA : kerbB) : k === 4 ? (stripe ? turfA : turfB) : Math.floor(t.s[i] / 2.5) % 3 === 0 ? tarB : tarA;
        band(aprons, side, i, in0, in1, in0 + width[i], in1 + width[j], Y_APRON, Y_APRON, c);
      }
    }
    group.add(aprons.mesh(trackMaterial()));
  }

  // --- chequered start/finish line across sample 0
  const chq = new Tris();
  const tx = t.tx[0];
  const ty = t.ty[0];
  const cols = 12;
  const rows = 2;
  const sq = (hw * 2) / cols;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const color = (r + c) % 2 === 0 ? PAL.cream : PAL.brown;
      const s0 = (r - rows / 2) * sq;
      const s1 = s0 + sq;
      const o0 = -hw + c * sq;
      const o1 = o0 + sq;
      const pt = (s: number, o: number): number[] => [t.px[0] + tx * s - ty * o, Y_LINE + 0.004 + t.elev[0], -(t.py[0] + ty * s + tx * o)];
      chq.quad(pt(s0, o0), pt(s1, o0), pt(s1, o1), pt(s0, o1), color);
    }
  }
  group.add(chq.mesh(trackMaterial()));
  return group;
}

// ---------------------------------------------------------------------------
// Dev overlays (F1 panel)
// ---------------------------------------------------------------------------

export interface DevOverlays {
  sectors: Group;
  curvature: Group;
  braking: Group;
  racingLine: Group;
}

export function buildDevOverlays(t: TrackData, lineX: number[], lineY: number[]): DevOverlays {
  const n = t.n;
  const y = 0.25;
  const line = (pts: number[], colors: number[], closed: boolean): Line => {
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(pts, 3));
    g.setAttribute('color', new Float32BufferAttribute(colors, 3));
    const l = new Line(g, new LineBasicMaterial({ vertexColors: true, depthTest: false, transparent: true }));
    l.renderOrder = 50;
    if (closed) l.computeLineDistances();
    return l;
  };

  // Sectors: centreline in three colours.
  const sectors = new Group();
  const sc = [PAL.red, PAL.ochre, PAL.cypress];
  {
    const pts: number[] = [];
    const cols: number[] = [];
    for (let i = 0; i <= n; i++) {
      const k = i % n;
      const sIdx = k < t.sectorIndex[0] ? 0 : k < t.sectorIndex[1] ? 1 : 2;
      pts.push(t.px[k], y + t.elev[k], -t.py[k]);
      cols.push(sc[sIdx].r, sc[sIdx].g, sc[sIdx].b);
    }
    sectors.add(line(pts, cols, false));
    // Sector boundary posts.
    const posts: number[] = [];
    for (const k of [0, t.sectorIndex[0], t.sectorIndex[1]]) {
      const ox = -t.ty[k] * (t.halfWidth + 2);
      const oy = t.tx[k] * (t.halfWidth + 2);
      const e = t.elev[k];
      posts.push(t.px[k] - ox, 0.1 + e, -(t.py[k] - oy), t.px[k] + ox, 0.1 + e, -(t.py[k] + oy));
      posts.push(t.px[k] + ox, 0.1 + e, -(t.py[k] + oy), t.px[k] + ox, 5 + e, -(t.py[k] + oy));
    }
    const pg = new BufferGeometry();
    pg.setAttribute('position', new Float32BufferAttribute(posts, 3));
    const ls = new LineSegments(pg, new LineBasicMaterial({ color: PAL.brown, depthTest: false }));
    ls.renderOrder = 51;
    sectors.add(ls);
  }

  // Curvature: centreline shaded from cream (straight) to red (tight).
  const curvature = new Group();
  {
    const pts: number[] = [];
    const cols: number[] = [];
    for (let i = 0; i <= n; i++) {
      const k = i % n;
      const a = Math.min(1, Math.abs(t.curvature[k]) * 30);
      const c = PAL.cream.clone().lerp(PAL.red, a);
      pts.push(t.px[k], y + 0.05 + t.elev[k], -t.py[k]);
      cols.push(c.r, c.g, c.b);
    }
    curvature.add(line(pts, cols, false));
  }

  // Braking zones: from the braking point to the corner, plus a post at the corner.
  const braking = new Group();
  {
    const segs: number[] = [];
    for (const z of t.brakingZones) {
      const len = Math.round(Math.min(150, z.speedDrop * 4.5) / (t.length / n));
      for (let q = 0; q < len; q++) {
        const a = (z.cornerIndex - len + q + n) % n;
        const b = (a + 1) % n;
        segs.push(t.px[a], y + 0.1 + t.elev[a], -t.py[a], t.px[b], y + 0.1 + t.elev[b], -t.py[b]);
      }
      const k = z.cornerIndex;
      segs.push(t.px[k], 0.1 + t.elev[k], -t.py[k], t.px[k], 7 + t.elev[k], -t.py[k]);
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(segs, 3));
    const ls = new LineSegments(g, new LineBasicMaterial({ color: PAL.red, depthTest: false }));
    ls.renderOrder = 52;
    braking.add(ls);
  }

  // Bot racing line.
  const racingLine = new Group();
  {
    const pts: number[] = [];
    const cols: number[] = [];
    for (let i = 0; i <= n; i++) {
      const k = i % n;
      pts.push(lineX[k], y + 0.02 + t.elev[k], -lineY[k]);
      cols.push(PAL.cypress.r, PAL.cypress.g, PAL.cypress.b);
    }
    racingLine.add(line(pts, cols, true));
  }
  for (const g of [sectors, curvature, braking, racingLine]) g.visible = false;
  return { sectors, curvature, braking, racingLine };
}
